/**
 * Master password helpers. The password is never stored — only a salted
 * SHA-256 digest, written to the account's Firestore security profile.
 */

const UNLOCK_STORAGE_PREFIX = 'student-recorder:unlocked:';
const HASH_STORAGE_PREFIX = 'student-recorder:master:';

export async function hashMasterPassword(uid: string, password: string): Promise<string> {
  const encoded = new TextEncoder().encode(`student-recorder::${uid}::${password}`);
  const digest = await crypto.subtle.digest('SHA-256', encoded);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

export function readUnlockFlag(uid: string): boolean {
  try {
    return localStorage.getItem(`${UNLOCK_STORAGE_PREFIX}${uid}`) === 'true';
  } catch {
    return false;
  }
}

export function writeUnlockFlag(uid: string, unlocked: boolean): void {
  try {
    if (unlocked) {
      localStorage.setItem(`${UNLOCK_STORAGE_PREFIX}${uid}`, 'true');
    } else {
      localStorage.removeItem(`${UNLOCK_STORAGE_PREFIX}${uid}`);
    }
  } catch {
    /* storage unavailable — the session simply won't persist */
  }
}

/**
 * The digest is mirrored on the device so the security gate keeps working even
 * when Firestore is unreachable. It is the same salted SHA-256 value.
 */
export function readLocalHash(uid: string): string | null {
  try {
    return localStorage.getItem(`${HASH_STORAGE_PREFIX}${uid}`);
  } catch {
    return null;
  }
}

export function writeLocalHash(uid: string, hash: string): void {
  try {
    localStorage.setItem(`${HASH_STORAGE_PREFIX}${uid}`, hash);
  } catch {
    /* storage unavailable */
  }
}

export interface PasswordStrength {
  score: number;
  label: string;
  tone: 'weak' | 'fair' | 'good' | 'strong';
}

export function scorePassword(password: string): PasswordStrength {
  let score = 0;
  if (password.length >= 6) score += 1;
  if (password.length >= 10) score += 1;
  if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score += 1;
  if (/\d/.test(password)) score += 1;
  if (/[^A-Za-z0-9]/.test(password)) score += 1;

  if (!password) return { score: 0, label: 'Empty', tone: 'weak' };
  if (score <= 1) return { score: 1, label: 'Weak', tone: 'weak' };
  if (score === 2) return { score: 2, label: 'Fair', tone: 'fair' };
  if (score === 3 || score === 4) return { score: 3, label: 'Good', tone: 'good' };
  return { score: 4, label: 'Strong', tone: 'strong' };
}
